// A stonemason, described the way age-of-mcp authors it: bench / block /
// grindstone / cut-blocks / shelter helpers plus the shared `foundationSlab` and
// `scaffold`, walked through its build stages. Ported 1:1 from age-of-mcp's
// stonemason.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const WOOD  = "#7a5a30";
const FRAME = "#5e4423";
const STONE = "#9a9a9a";
const DARK  = "#6f6f6f";
const ROOF  = "#4f4f4f";

// The cutting bench the block is dressed on.
function bench(): Part {
  return { geometry: new THREE.BoxGeometry(0.7, 0.18, 0.5), color: WOOD, position: [0, 0.23, 0] };
}

// The rough stone block being dressed on the bench.
function block(): Part {
  return { geometry: new THREE.BoxGeometry(0.34, 0.26, 0.3), color: STONE, position: [0.04, 0.45, 0] };
}

// A grindstone wheel mounted upright on the side (axle along z). Named so the
// "active" animation can turn it about its axle.
function grindstone(): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.18, 0.18, 0.06, 14),
    color: DARK,
    position: [-0.3, 0.36, 0.12],
    rotation: [Math.PI / 2, 0, 0],
    name: "grindstone",
  };
}

// A small stack of finished blocks in the corner.
function cutBlocks(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.2, 0.12, 0.2), color: STONE, position: [-0.28, 0.2, -0.28] },
    { geometry: new THREE.BoxGeometry(0.2, 0.12, 0.2), color: DARK,  position: [-0.28, 0.32, -0.28] },
  ];
}

// An open shelter: four corner posts under a flat plank roof.
function shelter(): Part[] {
  const parts: Part[] = [];
  for (const x of [-0.32, 0.32]) {
    for (const z of [-0.32, 0.32]) {
      parts.push({
        geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.55, 6),
        color: FRAME,
        position: [x, 0.14 + 0.275, z],
      });
    }
  }
  parts.push({
    geometry: new THREE.BoxGeometry(0.92, 0.06, 0.92),
    color: ROOF,
    position: [0, 0.14 + 0.58, 0],
  });
  return parts;
}

// The build stages of the stonemason: foundation, bench with scaffolding, block
// added, and the finished cutting yard with grindstone, cut blocks and shelter.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), bench(), ...scaffold(0.84, 0.6)];
  if (s === 2) return [foundationSlab(0.86), bench(), block(), ...scaffold(0.84, 1.05)];
  return [foundationSlab(0.86), bench(), block(), grindstone(), ...cutBlocks(), ...shelter()];
}

export const stonemason = defineModel({
  id: "stonemason",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Stonemason",
  tags: ["building","stonemason","low-poly","age-of-mcp","classic","animated"],
  summary: "Low-poly stonemason.",
  description: "A mason's cutting bench that cuts rough stone into dressed blocks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Grindstone turning",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [spin("grindstone", [-0.3, 0.36, 0.12], { turns: 2 })],
    },
  ],
  stations: [
    { id: "bench-front", position: [0, 0.14, 0.42], facing: Math.PI, description: "Mason's spot, front of the bench." },
    { id: "bench-back", position: [0, 0.14, -0.42], facing: 0, description: "Mason's spot, back of the bench." },
  ],
  interactions: [
    { id: "carve", label: "Carving stone", station: "bench-front", variants: ["complete"], ...benchWork() },
  ],
});
